import { inject, Injectable, signal } from '@angular/core';
import {
  AcceptReferralPayload,
  AcceptReferralResponse,
  CandidateInfo,
  CompanyTalentScoutInfo,
  Job,
  JobListPage,
  CustomTracking
} from '../data-access/job-share.models';
import { BehaviorSubject, Observable, of, Subject, throwError } from 'rxjs';
import { RecaptchaComponent } from 'ng-recaptcha';
import { HttpClient } from '@angular/common/http';
import { UIRouterGlobals } from '@uirouter/core';
import { catchError, first, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { removeEmptyProperties } from '@utils/object-utils';
import { environment } from '@environments/environment';
import { CompanyService } from '@app/shared/services/company.service';
import { featureFlags } from '@utils/feature-flags';
import { RecaptchaService } from '@shared/modules/recaptcha/recaptcha.service';
import { IFBLocation, IFBReferralApplication, IFBTokenInfoV2 } from '@interfaces';
import { JobResource } from '@app/shared/modules/resources/job.resource';
import { JobApplicationResource } from '@app/shared/modules/resources/job-application.resource';
import { ReferralResource } from '@app/shared/modules/resources/referral.resource';
import { storage } from '@utils/storage.service';

@Injectable({
  providedIn: 'root'
})
export class JobShareListService {
  private readonly http = inject(HttpClient);
  private readonly recaptchaService = inject(RecaptchaService);
  private readonly uiRouterGlobals = inject(UIRouterGlobals);
  private readonly companyService = inject(CompanyService);
  private readonly jobResource = inject(JobResource);
  private readonly jobApplicationResource = inject(JobApplicationResource);
  private readonly referralResource = inject(ReferralResource);
  private readonly companyId = this.companyService.data.id;
  private readonly url = `https://${environment.CANDIDATE_BASE}/v1`;
  private readonly filterCriteria = new BehaviorSubject<{ jobName: string; jobLocationName: string }>({
    jobName: '',
    jobLocationName: ''
  });
  private infoCompanyTalentScout$: Observable<CompanyTalentScoutInfo>;
  private tokenInfo$: Observable<IFBTokenInfoV2>;
  private clickId: string;
  private visitorIdHeaderName = 'X-Visitor-Id';
  filterCriteriaChanged$ = this.filterCriteria.asObservable();
  get sharedJobId(): string {
    return this.uiRouterGlobals.params.jobId;
  }
  get currentJobId(): string {
    return this.uiRouterGlobals.params.currentJob;
  }
  get shareToken(): string {
    return this.uiRouterGlobals.params.st;
  }
  get prt(): string {
    return this.uiRouterGlobals.params.prt;
  }
  selectedJobId = signal<string>(this.uiRouterGlobals.params.currentJob || this.uiRouterGlobals.params.jobId);
  selectedJob = signal<Job>(null);
  numberOfJobs = signal(0);
  resetFilters$ = new Subject<void>();
  noJobsFound = signal(false);
  errorCaptcha = signal(false);
  shouldRecaptchaMount = false;
  isLoading = signal(true);
  userAcceptedNotification = true;
  candidateDetails: CandidateInfo;

  getInfoCompanyTalentScout(prt = this.uiRouterGlobals.params.prt) {
    if (!this.infoCompanyTalentScout$) {
      this.infoCompanyTalentScout$ = this.http
        .get<CompanyTalentScoutInfo>(`${this.url}/jobs-page/info`, { headers: this.getHeaders(prt) })
        .pipe(shareReplay(1));
    }

    return this.infoCompanyTalentScout$;
  }

  getTokenInfoV1(referraltoken = this.uiRouterGlobals.params.referraltoken) {
    return this.referralResource.getTokenInfo(referraltoken).pipe(
      first(),
      tap(candidateInfo => {
        this.candidateDetails = candidateInfo.candidateInfo;
      })
    );
  }

  getTokenInfoV2(shareTokenOrPersonalReferralToken = this.uiRouterGlobals.params.prt) {
    if (!this.tokenInfo$) {
      this.tokenInfo$ = this.referralResource.getTokenInfoV2(shareTokenOrPersonalReferralToken).pipe(shareReplay(1));
    }

    return this.tokenInfo$;
  }

  getReferrer() {
    return this.getInfoCompanyTalentScout().pipe(map(info => info.referral.talentscout));
  }

  getJobList(query: { limit: number; offset?: number; title?: string; locations?: string; jobs?: string }) {
    const jobsParam =
      query.jobs ??
      (this.currentJobId ? [this.sharedJobId, this.currentJobId].filter(Boolean).join(',') : this.sharedJobId);
    const params = { ...removeEmptyProperties({ ...query, jobs: jobsParam, prioritizeJobLocation: this.sharedJobId }) };
    return this.http.get<JobListPage>(`${this.url}/jobs/${this.companyId}`, { params, headers: this.getHeaders() });
  }

  getLocations() {
    return this.http.get<IFBLocation[]>(`${this.url}/locations/${this.companyId}`, {
      headers: this.getHeaders()
    });
  }

  emitFilterCriteriaChange(filter: { jobName: string; jobLocationName: string }) {
    this.filterCriteria.next(filter);
  }

  getFilterCriteria() {
    return this.filterCriteria.value;
  }

  resetFilters() {
    this.filterCriteria.next({ jobName: '', jobLocationName: '' });
    this.resetFilters$.next();
    this.noJobsFound.set(false);
  }

  acceptReferral(payload: AcceptReferralPayload) {
    const jobId = this.selectedJobId();
    const bearerToken = this.shareToken ?? this.uiRouterGlobals.params.prt;
    const headers = this.getHeaders(bearerToken);
    const subdomain = this.companyService.subdomain;
    const domain = this.companyService.domain(environment.API_BASE);

    return this.http
      .post<AcceptReferralResponse>(
        `https://${subdomain}.referral.${domain}/api/candidate/v2/referral/${jobId}`,
        payload,
        {
          headers
        }
      )
      .pipe(
        tap(
          () =>
            (this.candidateDetails = { email: payload.email, firstName: payload.firstName, lastName: payload.lastName })
        )
      );
  }

  addAdditionalInfo(payload: IFBReferralApplication, recaptchaToken: string) {
    return this.jobApplicationResource.create(payload, recaptchaToken);
  }

  getCustomFormTemplate() {
    return this.jobResource.getApplicationFormTemplate(this.selectedJobId()).pipe(catchError(_ => of(null)));
  }

  isRecaptchaEnabled() {
    this.errorCaptcha.set(false);
    return this.recaptchaService.isEnabled;
  }

  isRecaptchaEnabledPersonalInfo(forceRecaptchaMount = false) {
    return (
      this.isRecaptchaEnabled() &&
      !featureFlags.isOn('UI.disableViewTracking') &&
      (this.shouldRecaptchaMount || forceRecaptchaMount)
    );
  }

  checkRecaptcha(recaptchaComponent?: RecaptchaComponent, checkIfUserHasAcceptedNotification = true) {
    return this.recaptchaService.execute(recaptchaComponent).pipe(
      tap(() => this.errorCaptcha.set(false)),
      switchMap(token => this.captchaVerified(token, checkIfUserHasAcceptedNotification))
    );
  }

  startTrackingBirdlyParameter() {
    if (!this.uiRouterGlobals.params.birdly && this.uiRouterGlobals.params.prt) {
      const trackingUrl = `https://${environment.SHORTENER_BASE}/api/track/${this.uiRouterGlobals.params.prt}?redirect=${encodeURIComponent(window.location.pathname)}`;

      const visitorIdHeader = this.getVisitorIdHeader();
      return this.http
        .post<{ redirectUrl: string }>(trackingUrl, {}, { observe: 'response', headers: visitorIdHeader })
        .pipe(
          tap(res => {
            if (!visitorIdHeader[this.visitorIdHeaderName]) {
              this.saveVisitorIdHeader(res.headers.get(this.visitorIdHeaderName));
            }
          }),
          map(response => response.body)
        );
    }
    return of({ redirectUrl: '' });
  }

  tracking(payload: Partial<CustomTracking>) {
    const clickId = this.getClickId();
    if (!clickId) {
      return of({});
    }
    payload = {
      ...payload,
      personalReferralToken: this.uiRouterGlobals.params.prt,
      sessionData: {
        clickId
      }
    };
    return this.http.post(`${this.url}/track`, payload, { headers: this.getHeaders() });
  }

  private getClickId() {
    if (!this.clickId) {
      try {
        const urlDecoded = decodeURIComponent(this.uiRouterGlobals.params.birdly);
        const base64Decoded = atob(urlDecoded);
        const params = new URLSearchParams(base64Decoded);
        this.clickId = params.get('birdly-click');
      } catch (error) {
        this.clickId = null;
      }
    }

    return this.clickId;
  }

  private captchaVerified(token: string, checkIfUserHasAcceptedNotification: boolean) {
    if (!this.recaptchaService.isEnabled || (checkIfUserHasAcceptedNotification && !this.userAcceptedNotification)) {
      return of('');
    }

    if (token === '') {
      this.errorCaptcha.set(true);
      return throwError(() => null);
    }

    return of(token);
  }

  private getHeaders(prt = this.uiRouterGlobals.params.prt) {
    const headers = {
      Authorization: `Bearer ${prt}`,
      'X-1brd-Host': this.companyService.x1brdHost
    };

    return headers;
  }

  private getVisitorIdHeader() {
    const visitorId = storage.getItem(this.visitorIdHeaderName);
    if (visitorId && new Date(visitorId.dateExpired) > new Date()) {
      return { [this.visitorIdHeaderName]: visitorId.value };
    }
    return {};
  }

  private saveVisitorIdHeader(visitorId: string) {
    if (visitorId) {
      const oneYearFromNow = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000);
      storage.setItem(this.visitorIdHeaderName, {
        value: visitorId,
        dateExpired: oneYearFromNow
      });
    }
  }
}
